import { computed, onUnmounted, Ref, ref, set, watch } from "@vue/composition-api";
import { LiveChatJoinResponse } from "@scrile/api-provider/dist/api/LivechatsProvider";
import { StreamingHelper } from "@scrile/streaming-client";
import providers from "@/lib/api";
import { LoggerContextWithMessage } from "@/modules/callsLogger";
import useStreamLogger from "./useStreamLogger";
import { initDebugInfo } from "../helpers";
import { StreamUserData } from "../types";

export default function useConsume({
  joinData,
  getLoggerContext,
}: {
  joinData: Ref<LiveChatJoinResponse | null>;
  getLoggerContext: () => Record<string, any>;
}) {
  const consumeUserIds = ref<string[]>([]);
  const users = ref<Record<string, StreamUserData>>({});
  const connecting = ref<Record<string, boolean>>({});
  const connectingState = ref<Record<string, string>>({});

  const usersToConsume = computed(() => Object.values(users.value));

  const getAdditionalContext = (user: StreamUserData): Record<string, any> => {
    return {
      stream: { id: user.stream.id, active: user.stream.active },
      connection: user.connection,
    };
  };

  const { businessLogicLog, collectErrorLogs, debug } = useStreamLogger("[CONSUME CONTROLLER]", getLoggerContext);

  const updateUser = (broadcasterChatUserId: string, { stream, ...data }: Partial<StreamUserData>) => {
    const user = joinData.value?.users.find(i => i.id === broadcasterChatUserId);
    if (!user) return;

    users.value = {
      ...users.value,
      [broadcasterChatUserId]: {
        ...users.value[broadcasterChatUserId],
        user,
        ...data,
        isSpeaking: data.constraints?.audio && data.isSpeaking,
        stream: stream ?? new MediaStream([]),
      },
    };
  };

  const updateUserSpeaking = (broadcasterChatUserId: string, isSpeaking: boolean) => {
    users.value = {
      ...users.value,
      [broadcasterChatUserId]: {
        ...users.value[broadcasterChatUserId],
        isSpeaking,
      },
    };
  };

  const removeUser = (broadcasterChatUserId: string) => {
    const user = users.value[broadcasterChatUserId];
    if (!user) return;
    businessLogicLog("Close stream", getAdditionalContext(user as StreamUserData));
    user.WebRTC.closeAllStreams();

    users.value = Object.entries(users.value).reduce((acc, [id, entry]) => {
      if (id !== broadcasterChatUserId) acc[id] = entry as StreamUserData;
      return acc;
    }, {} as Record<string, StreamUserData>);
    consumeUserIds.value = consumeUserIds.value.filter(i => i !== broadcasterChatUserId);
  };

  const consumeStream = async (broadcasterChatUserId: string, token: string) => {
    if (consumeUserIds.value.includes(broadcasterChatUserId)) return;
    try {
      set(connecting.value, broadcasterChatUserId, true);
      const connection = await providers.LivechatsProvider.watch({
        token,
        broadcasterChatUserId,
      });
      const WebRTC = new StreamingHelper();
      const stream = await WebRTC.consumeStream({
        connection,
        tracks: { audio: true, video: true },
      });
      const constraints = { audio: true, video: true };
      const additionalContext: Record<string, any> = {
        stream: { id: stream.id, active: stream.active },
        connection: connection,
      };
      businessLogicLog("Consume stream", additionalContext);
      updateUser(broadcasterChatUserId, { stream, connection, constraints, WebRTC });
      updateUserSpeaking(broadcasterChatUserId, false);
      consumeUserIds.value = [...consumeUserIds.value, broadcasterChatUserId];
    } catch (error) {
      collectErrorLogs("Consume stream", error);
      consumeUserIds.value = consumeUserIds.value.filter(id => id !== broadcasterChatUserId);
    } finally {
      set(connecting.value, broadcasterChatUserId, false);
    }
  };

  const closeAllStreams = () => {
    for (const userId of Object.keys(users.value)) {
      removeUser(userId);
    }
  };

  const addWebRTCWatchers = (broadcasterChatUserId: string) => {
    const user = users.value[broadcasterChatUserId];
    if (!user) return;
    initDebugInfo(user.WebRTC as StreamingHelper);
    user.WebRTC.logger.on("logging", ({ message, ...others }: LoggerContextWithMessage) =>
      debug(`[STREAM CLIENT] ${message}`, others)
    );
    user.WebRTC.on("connectionstatechange", (state: string) => {
      set(connectingState.value, broadcasterChatUserId, state);
    });
    user.WebRTC.on("audio-activity", ({ isSpeaking }: { isSpeaking: boolean }) => {
      updateUserSpeaking(broadcasterChatUserId, isSpeaking);
    });
    user.WebRTC.on("producers-updated", async ({ video, audio }: { video: boolean; audio: boolean }) => {
      if (!user.connection) return;
      const context = getAdditionalContext(user as StreamUserData);

      businessLogicLog("Producers updating", context);
      updateUser(broadcasterChatUserId, { constraints: { video, audio } });
      try {
        const stream = await user.WebRTC.consumeStream({
          connection: user.connection,
          tracks: { video, audio },
        });
        updateUser(broadcasterChatUserId, { stream });
      } catch (error) {
        collectErrorLogs("Producers updating", error);
      }
    });
  };

  watch(consumeUserIds, (value, oldValue) => {
    value
      .filter(i => !oldValue.includes(i))
      .forEach(id => {
        addWebRTCWatchers(id);
      });
  });

  onUnmounted(closeAllStreams);

  return {
    usersToConsume,
    connecting,
    connectingState,
    consumeStream,
    removeUser,
    closeAllStreams,
  };
}
